import * as Application from 'expo-application'
import Constants from 'expo-constants'
import * as Crypto from 'expo-crypto'
import * as Device from 'expo-device'
import * as Localization from 'expo-localization'
import { AppState, Platform } from 'react-native'
import { env } from '../env'
import { isIsolatedPreviewBuild } from '../runtime-build'
import { SecureStorageAdapter } from '../storage/secure-storage'
import { supabase } from '../supabase'

type ProductEventType =
  | 'screen_view'
  | 'press'
  | 'scroll_depth'
  | 'form_focus'
  | 'form_submit'
  | 'search'
  | 'filter'
  | 'booking_step'
  | 'listing_step'
  | 'chat'
  | 'payment_step'
  | 'system'
  | 'performance'

type ProductTelemetryEvent = {
  clientEventId: string
  eventName: string
  eventType: ProductEventType
  occurredAt: string
  screenName?: string | null
  routePath?: string | null
  elementId?: string | null
  elementType?: string | null
  scrollDepthPct?: number | null
  durationMs?: number | null
  metadata?: Record<string, unknown>
}

type DeviceContext = {
  appVersion?: string | null
  deviceType?: string | null
  locale?: string | null
  platform: string
  timezone?: string | null
}

const SESSION_ID = `product-${Date.now().toString(36)}-${randomId()}`
const INSTALLATION_ID_KEY = 'tago.security.installation_id'
const FLUSH_INTERVAL_MS = 5_000
const FAST_FLUSH_INTERVAL_MS = 1_500
const FLUSH_BATCH_SIZE = 12
const MAX_BATCH_SIZE = 30
const MAX_QUEUE_SIZE = 180
const PRESS_DEDUPE_MS = 180
/**
 * How often the app says it is still alive while foregrounded. 30s is a
 * compromise: fine enough to bracket a freeze to within half a minute, coarse
 * enough that it costs ~120 events per hour of active use.
 */
const HEARTBEAT_INTERVAL_MS = 30_000

let queue: ProductTelemetryEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let flushDueAt = 0
let flushing = false
let lifecycleStarted = false
let deviceContextPromise: Promise<DeviceContext> | null = null
let installationIdPromise: Promise<string> | null = null
let currentRoutePath: string | null = null
let currentScreenName: string | null = null
let lastScreenViewKey: string | null = null
let lastScreenViewAt = 0
const lastPressAtByKey = new Map<string, number>()

function enabled(): boolean {
  // Preview is a disposable fixture app. Its product analytics must neither
  // pollute the production funnel nor keep retrying an optional fixture
  // function that may not be deployed; runtime identity wins over an
  // accidentally inherited public environment flag.
  return env.isProductTelemetryEnabled && !env.isMisconfigured && !isIsolatedPreviewBuild
}

function randomId(): string {
  if (typeof Crypto.randomUUID === 'function') return Crypto.randomUUID()
  return Array.from(Crypto.getRandomBytes(16))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

function nowIso(): string {
  return new Date().toISOString()
}

function safeText(value: unknown, max = 96): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(trimmed) ||
    /\+?\d[\d\s().-]{7,}\d/.test(trimmed)
  ) {
    return '[redacted]'
  }
  return trimmed
    .replace(/[^\w:./@-]+/g, '_')
    .replace(/_{2,}/g, '_')
    .slice(0, max)
}

function safeLabelText(value: unknown, max = 120): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(trimmed) ||
    /\+?\d[\d\s().-]{7,}\d/.test(trimmed)
  ) {
    return '[redacted]'
  }
  return trimmed.replace(/\s+/g, ' ').slice(0, max)
}

function slugText(value: unknown, fallback = 'item', max = 72): string {
  const label = safeLabelText(String(value ?? ''), max) ?? fallback
  const slug = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, max)
  return slug || fallback
}

function labelFromAccessibilityFallback(value: string | null): string | null {
  if (!value?.startsWith('a11y.')) return null
  const raw = value
    .replace(/^a11y[._-]/i, '')
    .replace(/_/g, ' ')
    .trim()
  if (/^atr.s$/i.test(raw) || /^atr[aá]s$/i.test(raw) || /^back$/i.test(raw)) return 'Volver'
  return safeLabelText(raw)
}

function stableAutoElementId(label: string | null, elementType: string | null): string {
  const routeSlug = slugText(currentRoutePath ?? currentScreenName ?? 'screen', 'screen', 84)
  const labelSlug = slugText(label ?? elementType ?? 'pressable', 'pressable', 48)
  return `${routeSlug}.${labelSlug}`.slice(0, 120)
}

function screenLabel(route: string): string {
  const normalized = normalizeRoute(route) ?? route
  switch (normalized) {
    case '/verify-phone':
      return 'Verificación telefónica'
    case '/welcome':
      return 'Bienvenida'
    case '/profile':
      return 'Perfil'
    case '/register':
      return 'Registro'
    case '/login':
      return 'Inicio de sesión'
    default:
      return (
        safeLabelText(
          normalized
            .replace(/^\//, '')
            .replace(/\((auth|tabs)\)\//g, '')
            .replace(/\[(.*?)\]/g, '$1')
            .replace(/[._/-]+/g, ' '),
        ) ?? 'Pantalla'
      )
  }
}

function normalizeRoute(route: string | null | undefined): string | null {
  if (!route) return null
  return route
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
    .replace(/\/[A-Za-z0-9_-]{16,}(?=\/|$)/g, '/:id')
    .slice(0, 180)
}

function routeToScreen(route: string | null | undefined): string | null {
  const normalized = normalizeRoute(route)
  if (!normalized) return null
  const cleaned = normalized
    .replace(/^\//, '')
    .replace(/\((auth|tabs)\)\//g, '')
    .replace(/\[(.*?)\]/g, ':$1')
  return safeText(cleaned || 'root', 96)
}

function boundedNumber(value: number | null | undefined, min: number, max: number): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return Math.min(max, Math.max(min, Math.round(value)))
}

function shouldFastFlush(event: ProductTelemetryEvent): boolean {
  return !['screen_view', 'press', 'scroll_depth', 'performance'].includes(event.eventType)
}

async function installationId(): Promise<string> {
  if (installationIdPromise) return installationIdPromise
  installationIdPromise = (async () => {
    const existing = await SecureStorageAdapter.getItem(INSTALLATION_ID_KEY)
    if (existing) return existing
    const next = `install-${randomId()}`
    await SecureStorageAdapter.setItem(INSTALLATION_ID_KEY, next)
    return next
  })()
  return installationIdPromise
}

function scheduleFlush(delayMs = FLUSH_INTERVAL_MS): void {
  if (!enabled()) return
  const dueAt = Date.now() + delayMs
  if (flushTimer && flushDueAt <= dueAt) return
  if (flushTimer) clearTimeout(flushTimer)
  flushDueAt = dueAt
  flushTimer = setTimeout(() => {
    flushTimer = null
    flushDueAt = 0
    void productTelemetryService.flush()
  }, delayMs)
}

function push(event: ProductTelemetryEvent): void {
  if (!enabled()) return
  queue.push(event)
  if (queue.length > MAX_QUEUE_SIZE) {
    queue = queue.slice(queue.length - MAX_QUEUE_SIZE)
  }

  if (queue.length >= FLUSH_BATCH_SIZE) {
    void productTelemetryService.flush()
  } else {
    scheduleFlush(shouldFastFlush(event) ? FAST_FLUSH_INTERVAL_MS : FLUSH_INTERVAL_MS)
  }
}

async function getDeviceContext(): Promise<DeviceContext> {
  if (deviceContextPromise) return deviceContextPromise
  deviceContextPromise = (async () => {
    const deviceType = await Device.getDeviceTypeAsync().catch(() => null)
    const locale = Localization.getLocales()[0]?.languageTag ?? null
    const timezone =
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : (Localization.getCalendars()[0]?.timeZone ?? null)

    return {
      appVersion: Application.nativeApplicationVersion ?? Constants.expoConfig?.version ?? null,
      deviceType: deviceType == null ? null : String(deviceType),
      locale,
      platform: Platform.OS,
      timezone,
    }
  })()
  return deviceContextPromise
}

async function currentAccessToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  } catch {
    return null
  }
}

function compactMetadata(metadata?: Record<string, unknown>): Record<string, unknown> {
  if (!metadata) return {}
  const output: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(metadata).slice(0, 16)) {
    const safeKey = safeText(key, 48)
    if (!safeKey) continue
    if (
      /password|token|secret|otp|email|phone|card|cvv|dui|document|license|address/i.test(safeKey)
    ) {
      output[safeKey] = '[redacted]'
      continue
    }
    if (typeof value === 'string') {
      output[safeKey] =
        safeKey === 'elementLabel' ? safeLabelText(value, 120) : safeText(value, 100)
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      output[safeKey] = value
    } else if (typeof value === 'boolean' || value === null) {
      output[safeKey] = value
    }
  }
  return output
}

function createEvent(
  eventType: ProductEventType,
  eventName: string,
  payload: Omit<ProductTelemetryEvent, 'clientEventId' | 'eventName' | 'eventType' | 'occurredAt'>,
): ProductTelemetryEvent | null {
  const safeEventName = safeText(eventName, 96)
  if (!safeEventName) return null

  return {
    clientEventId: `evt-${Date.now().toString(36)}-${randomId()}`,
    eventName: safeEventName,
    eventType,
    occurredAt: nowIso(),
    screenName: safeText(payload.screenName ?? currentScreenName, 96),
    routePath: normalizeRoute(payload.routePath ?? currentRoutePath),
    elementId: safeText(payload.elementId, 120),
    elementType: safeText(payload.elementType, 48),
    scrollDepthPct: boundedNumber(payload.scrollDepthPct, 0, 100),
    durationMs: boundedNumber(payload.durationMs, 0, 86_400_000),
    metadata: compactMetadata(payload.metadata),
  }
}

function startBrowserLifecycle(): () => void {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return () => {}
  const handleVisibility = () => {
    if (document.visibilityState === 'hidden') void productTelemetryService.flush()
  }
  const handlePageHide = () => {
    void productTelemetryService.flush()
  }
  document.addEventListener('visibilitychange', handleVisibility)
  window.addEventListener('pagehide', handlePageHide)
  return () => {
    document.removeEventListener('visibilitychange', handleVisibility)
    window.removeEventListener('pagehide', handlePageHide)
  }
}

export const productTelemetryService = {
  startLifecycle(): () => void {
    if (lifecycleStarted) return () => {}
    lifecycleStarted = true
    const appStateSubscription = AppState.addEventListener('change', (state) => {
      if (state !== 'active') void productTelemetryService.flush()
    })
    const stopBrowserLifecycle = startBrowserLifecycle()

    // Liveness heartbeat.
    //
    // This is the single missing primitive that separates "the app froze" from
    // "the user put the phone down". Today those two are indistinguishable in
    // every system: the Sentry session ends `healthy` either way, Play vitals
    // stay clean either way, product events simply stop either way, and the
    // admin friction score DROPS in both cases because it counts interactions.
    //
    // Heartbeats continuing while presses are zero is a freeze. Both stopping
    // is a person walking away. One timer, and the difference becomes visible.
    const heartbeat = setInterval(() => {
      if (AppState.currentState !== 'active') return
      productTelemetryService.recordEvent('system', 'app.alive', {
        elementId: 'liveness.heartbeat',
        elementType: 'diagnostic',
      })
    }, HEARTBEAT_INTERVAL_MS)

    return () => {
      appStateSubscription.remove()
      stopBrowserLifecycle()
      clearInterval(heartbeat)
      lifecycleStarted = false
    }
  },

  setCurrentRoute(routePath: string | null | undefined): void {
    currentRoutePath = normalizeRoute(routePath)
    currentScreenName = routeToScreen(routePath)
  },

  recordScreenView(routePath: string | null | undefined, metadata?: Record<string, unknown>): void {
    const normalizedRoute = normalizeRoute(routePath)
    const screenName = routeToScreen(normalizedRoute)
    if (!normalizedRoute || !screenName) return

    currentRoutePath = normalizedRoute
    currentScreenName = screenName

    const key = `${normalizedRoute}:${screenName}`
    const now = Date.now()
    if (lastScreenViewKey === key && now - lastScreenViewAt < 900) return
    lastScreenViewKey = key
    lastScreenViewAt = now

    const event = createEvent('screen_view', 'screen.viewed', {
      routePath: normalizedRoute,
      screenName,
      elementId: `screen.${slugText(normalizedRoute, 'route', 96)}`,
      elementType: 'screen',
      metadata: {
        ...metadata,
        elementIdQuality: 'screen',
        elementLabel: screenLabel(normalizedRoute),
      },
    })
    if (event) push(event)
  },

  recordPress(payload: {
    elementId?: string | null
    elementType?: string | null
    routePath?: string | null
    screenName?: string | null
    metadata?: Record<string, unknown>
  }): void {
    const rawElementId = safeText(payload.elementId, 120)
    const elementType = safeText(payload.elementType ?? 'pressable', 48)
    const metadataLabel =
      safeLabelText(payload.metadata?.elementLabel) ?? labelFromAccessibilityFallback(rawElementId)
    const explicitStableId = rawElementId && !rawElementId.startsWith('a11y.') ? rawElementId : null
    const elementId = explicitStableId ?? stableAutoElementId(metadataLabel, elementType)
    const elementIdQuality = explicitStableId
      ? 'stable'
      : rawElementId?.startsWith('a11y.')
        ? 'stable_auto'
        : 'stable_auto'
    const elementLabel =
      metadataLabel ??
      labelFromAccessibilityFallback(rawElementId) ??
      safeLabelText(elementType?.replace(/[._-]+/g, ' ')) ??
      'Interacción'
    const key = `${currentRoutePath ?? '-'}:${elementId}`
    const now = Date.now()
    const previous = lastPressAtByKey.get(key) ?? 0
    if (now - previous < PRESS_DEDUPE_MS) return
    lastPressAtByKey.set(key, now)
    if (lastPressAtByKey.size > 120) lastPressAtByKey.clear()

    const event = createEvent('press', 'ui.pressed', {
      ...payload,
      elementId,
      elementType,
      metadata: {
        ...payload.metadata,
        elementIdQuality,
        elementLabel,
      },
    })
    if (event) push(event)
  },

  recordScrollDepth(payload: {
    depthPct: number
    routePath?: string | null
    screenName?: string | null
    elementId?: string | null
  }): void {
    const depthPct = boundedNumber(payload.depthPct, 0, 100)
    if (depthPct == null) return
    const event = createEvent('scroll_depth', 'scroll.depth_reached', {
      routePath: payload.routePath,
      screenName: payload.screenName,
      elementId: payload.elementId ?? 'screen.scroll',
      elementType: 'scroll',
      scrollDepthPct: depthPct,
    })
    if (event) push(event)
  },

  recordEvent(
    eventType: ProductEventType,
    eventName: string,
    payload: Omit<
      ProductTelemetryEvent,
      'clientEventId' | 'eventName' | 'eventType' | 'occurredAt'
    > = {},
  ): void {
    const event = createEvent(eventType, eventName, payload)
    if (event) push(event)
  },

  async flush(): Promise<void> {
    if (!enabled() || flushing || queue.length === 0) return
    flushing = true
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
      flushDueAt = 0
    }

    const batch = queue.splice(0, MAX_BATCH_SIZE)
    try {
      const [context, installId, accessToken] = await Promise.all([
        getDeviceContext(),
        installationId(),
        currentAccessToken(),
      ])
      await supabase.functions.invoke('record-product-events', {
        body: {
          installationId: installId,
          sessionId: SESSION_ID,
          events: batch.map((event) => ({ ...context, ...event })),
        },
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      })
    } catch (error) {
      if (__DEV__) console.warn('[productTelemetry] flush failed:', error)
      queue = [...batch, ...queue].slice(0, MAX_QUEUE_SIZE)
    } finally {
      flushing = false
      if (queue.length > 0) scheduleFlush()
    }
  },
}
